/**
 * TalentCardMedia — Iter-294d — Premium AI-ready media frame.
 *
 * One reusable media block for every Talent Discovery card. Handles:
 *   • Aspect-ratio constrained image with consistent cropping across
 *     all profiles (square by default, 4:5 portrait optionally).
 *   • Skeleton shimmer while the headshot is loading from the CDN /
 *     static mount — no broken-image flash on cold cache.
 *   • Automatic fallback to a sophisticated initial-based portrait
 *     when the network image fails (CDN miss, 404, decode error).
 *   • Forward-compatible media slots:
 *       - `videoUrl` → renders an in-corner play indicator that the
 *         parent card can wire to inline video / intro reels.
 *       - `liveAvatarUrl` → renders a "Live" pulse so when the AI
 *         avatar API ships (animated portraits, voice loop, etc.)
 *         we don't have to rebuild the card.
 *       - `assistantEnabled` → renders a small "Ask" affordance
 *         for the future AI booking assistant.
 *   • Children prop is a top-right overlay used by the parent for
 *     the favorite-toggle button only — all other badges live OUTSIDE
 *     this media frame to keep the headshot the visual focal point.
 *
 * Why a dedicated component:
 *   The user explicitly asked us to design for live avatars + intro
 *   videos + voice conversations + interactive portfolios + AI booking
 *   assistant WITHOUT another major redesign. Putting the media frame
 *   behind one prop-driven component is the cleanest seam to evolve.
 */
import React, { useMemo, useState } from 'react';
import { Play, Sparkles, MessageCircle } from 'lucide-react';

const ASPECT_CLASSES = {
  square: 'aspect-square',
  portrait: 'aspect-[4/5]',
  landscape: 'aspect-[5/4]',
};

// Vibrant palette pool — deterministic-per-name so the same talent
// always gets the same fallback background.
const FALLBACK_PALETTES = [
  ['from-violet-500',   'to-fuchsia-500'],
  ['from-cyan-500',     'to-blue-500'],
  ['from-rose-400',     'to-amber-400'],
  ['from-emerald-500',  'to-teal-500'],
  ['from-indigo-500',   'to-purple-500'],
  ['from-pink-500',     'to-rose-500'],
  ['from-orange-500',   'to-pink-500'],
  ['from-sky-500',      'to-indigo-500'],
];

function paletteFor(seed) {
  if (!seed) return FALLBACK_PALETTES[0];
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) {
    h = (h * 31 + seed.charCodeAt(i)) | 0;
  }
  return FALLBACK_PALETTES[Math.abs(h) % FALLBACK_PALETTES.length];
}

function initialsFor(name) {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?';
}

export function TalentCardMedia({
  photoUrl,
  displayName,
  aspect = 'square',
  videoUrl,
  liveAvatarUrl,
  assistantEnabled,
  showCategoryHint,        // optional faint chip on bottom-left of the media
  children,                // top-right overlay (typically the heart button)
  testId,
}) {
  const [hasError, setHasError] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const useFallback = !photoUrl || hasError;
  const palette = useMemo(() => paletteFor(displayName), [displayName]);
  const aspectClass = ASPECT_CLASSES[aspect] || ASPECT_CLASSES.square;

  return (
    <div
      className={`relative ${aspectClass} w-full overflow-hidden bg-gray-100`}
      data-testid={testId || 'talent-card-media'}
    >
      {/* Image OR fallback gradient with initials. */}
      {useFallback ? (
        <div
          className={`absolute inset-0 bg-gradient-to-br ${palette[0]} ${palette[1]} flex items-center justify-center`}
          data-testid={`${testId || 'talent-card-media'}-fallback`}
        >
          <span className="text-white text-5xl font-bold drop-shadow-md select-none">
            {initialsFor(displayName)}
          </span>
        </div>
      ) : (
        <>
          {/* Skeleton shimmer while loading. */}
          {!isLoaded && (
            <div
              className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-100 animate-pulse"
              aria-hidden
            />
          )}
          <img
            src={photoUrl}
            alt={displayName || 'Talent'}
            loading="lazy"
            decoding="async"
            // Render at intrinsic CSS pixels but request a 2× source so
            // the portrait stays sharp on high-DPR mobile displays.
            srcSet={`${photoUrl} 1x, ${photoUrl} 2x`}
            onLoad={() => setIsLoaded(true)}
            onError={() => setHasError(true)}
            className={`absolute inset-0 w-full h-full object-cover object-center transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
          />
        </>
      )}

      {/* Soft top→bottom gradient on the bottom 40% so any chips we put
          there remain legible regardless of headshot lighting. */}
      <div className="absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/55 to-transparent pointer-events-none" aria-hidden />

      {/* Top-right overlay (favorite button supplied by parent). */}
      {children && (
        <div className="absolute top-2 right-2 z-10 flex flex-col gap-1.5 items-end">
          {children}
        </div>
      )}

      {/* Future media slots — wired now so the future avatar work just
          flips a prop instead of restructuring the card. */}
      {liveAvatarUrl && (
        <div className="absolute top-2 left-2 flex items-center gap-1 bg-emerald-500/90 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full shadow-md backdrop-blur-sm">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          <span>Live avatar</span>
        </div>
      )}
      {videoUrl && (
        <div className="absolute bottom-2 right-2 flex items-center gap-1 bg-black/55 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
          <Play className="w-3 h-3 fill-white" />
          Intro reel
        </div>
      )}
      {assistantEnabled && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-violet-600/90 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
          <MessageCircle className="w-3 h-3" />
          Ask AI
        </div>
      )}

      {showCategoryHint && (
        <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-white/85 text-gray-900 text-[10px] font-semibold px-2 py-0.5 rounded-full backdrop-blur-sm">
          <Sparkles className="w-3 h-3 text-violet-500" />
          {showCategoryHint}
        </div>
      )}
    </div>
  );
}

export default TalentCardMedia;
